'use client';

import clsx from 'clsx';

import { useHandleQuickLink, type UrlTriggerEvent } from '../../hooks/UseNavigation';

import styles from './NavQuickLinkExhibitionsItem.module.scss';

export interface NavQuickLinkExhibitionsItemProps {
  /** System ID */
  sys?: { id: string };
  /** Background color for label box */
  backgroundColor?: string | null;
  /** Whether this item is the active swiper slide */
  isSwiperActive?: boolean;
  /** Navigation URL */
  url?: string | null;
  /** URL target (_self, _blank) */
  urlTarget?: string | null;
  /** Display text */
  urlText?: string | null;
  /** URL trigger event (opens modal instead of navigating) */
  urlTrigger?: UrlTriggerEvent | null;
  /** Optional className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

/**
 * NavQuickLinkExhibitionsItem - Exhibition timeline navigation item
 *
 * A timeline-style link used in exhibition navigation carousels.
 * Has separate desktop and mobile layouts.
 *
 * Features:
 * - Desktop: Box with label, scales on active
 * - Mobile: Compact text with underline on active
 * - Background color support
 */
export function NavQuickLinkExhibitionsItem({
  backgroundColor,
  className,
  isSwiperActive = false,
  url,
  urlTarget,
  urlText,
  urlTrigger,
  'data-testid': testId,
}: NavQuickLinkExhibitionsItemProps) {
  const { handleQuickLink } = useHandleQuickLink(url ?? '/', urlTarget ?? '_self', urlTrigger);

  return (
    <>
      {/* Desktop variant */}
      <div className={styles.desktopWrapper}>
        <button
          className={clsx(styles.wrapper, className)}
          onClick={handleQuickLink}
          data-testid={testId ?? 'NavQuickLinkExhibitionsItemWrapper'}
          type="button"
        >
          <span
            className={clsx(styles.labelBox, isSwiperActive && styles.active)}
            style={backgroundColor ? { backgroundColor } : undefined}
          >
            <span className={styles.label} data-testid="NavQuickLinkExhibitionsItemLabel">
              {urlText}
            </span>
          </span>
        </button>
      </div>

      {/* Mobile variant */}
      <div className={styles.mobileWrapper}>
        <button
          className={clsx(
            styles.mobileExhibitionWrapper,
            isSwiperActive && styles.active,
            className
          )}
          onClick={handleQuickLink}
          data-testid={testId ?? 'NavQuickLinkExhibitionsItemWrapper'}
          type="button"
        >
          <span
            className={styles.mobileLabel}
            style={backgroundColor ? { color: backgroundColor } : undefined}
            data-testid="NavQuickLinkExhibitionsItemLabel"
          >
            {urlText}
          </span>
        </button>
      </div>
    </>
  );
}

NavQuickLinkExhibitionsItem.displayName = 'NavQuickLinkExhibitionsItem';
